/**
 * Revision group header row for the Insights tab table.
 *
 * Renders a full-width section divider inside an antd Table body.
 * Shows: caret / revision label / Active badge / date range / per-revision subtotal summary.
 *
 * Returned as the `children` of a merged cell from the first dimension column's
 * `render` for a sentinel row. antd wraps this in the <td> and applies the colSpan
 * via the render's `props.colSpan` — so this component renders ONLY the cell's inner
 * content (no <tr>/<td> of its own).
 */

import React from 'react'
import { CaretDownOutlined, CaretRightOutlined } from '@ant-design/icons'
import type { RevisionMeta, RevisionSubtotals, JourneyPerformanceReport } from './revision-insights'
import { revisionDisplayLabel } from './revision-insights'
import { RevisionActiveTag } from './revision-active-tag'
import { numberWithCommas, numberWithPercent } from '../../../_utils/utils'

interface RevisionGroupHeaderProps {
    meta: RevisionMeta
    subtotals: RevisionSubtotals
    collapsed: boolean
    onToggle: () => void
    /**
     * How many table columns this header spans. Retained for callers/tests;
     * the actual cell colSpan is applied by antd via the column render's
     * `props.colSpan`, so this component no longer renders its own <td>.
     */
    colSpan: number
    /** Report type — controls which subtotal fields appear in the summary. */
    report: JourneyPerformanceReport
}

function buildSummaryText(subtotals: RevisionSubtotals, report: JourneyPerformanceReport): string {
    const fmt = numberWithCommas
    const pct = (v: number | null) => (v != null ? numberWithPercent(v) : '—')

    if (report === 'STEPS') {
        return `${fmt(subtotals.entrances)} entrants`
    }
    if (report === 'SPLIT_TEST_VARIANTS') {
        return `${fmt(subtotals.entrances)} entrants · ${fmt(subtotals.impressions)} impressions · CTR ${pct(subtotals.ctr)}`
    }
    // NOTIFICATIONS
    return `${fmt(subtotals.impressions)} impressions · CTR ${pct(subtotals.ctr)}`
}

export const RevisionGroupHeader: React.FC<RevisionGroupHeaderProps> = ({
    meta,
    subtotals,
    collapsed,
    onToggle,
    // colSpan is applied by antd on the wrapping <td>; not used directly here.
    colSpan: _colSpan,
    report,
}) => {
    return (
        <div
            className="revision-group-header-row"
            data-revision-id={meta.id}
            role="button"
            tabIndex={0}
            aria-expanded={!collapsed}
            aria-label={`${revisionDisplayLabel(meta)} revision group, ${collapsed ? 'collapsed' : 'expanded'}`}
            onClick={onToggle}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    onToggle()
                }
            }}
        >
            <div className="rgh-content">
                <span className="rgh-caret" aria-hidden={true}>
                    {collapsed ? <CaretRightOutlined /> : <CaretDownOutlined />}
                </span>
                <span className="rgh-tag">{revisionDisplayLabel(meta)}</span>
                {meta.isActive && <RevisionActiveTag />}
                <span className="rgh-date">· {meta.dateRange}</span>
                <span className="rgh-summary">{buildSummaryText(subtotals, report)}</span>
            </div>
        </div>
    )
}
